import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { FeedbackSectionId, UserType } from './utils';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  Answer,
  AnswersBySectionIdResponse,
  getAnswersBySectionId,
} from './api';
import { useClient } from 'shared/apollo';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import FeedbackImpactReceipt from './FeedbackImpactReceipt';

export type ReportAnswers = {
  [sectionId in FeedbackSectionId]?: Answer[];
};

export const answersBySection = (answers: Answer[]) => {
  const result: ReportAnswers = {};
  for (const a of answers) {
    if (!result[a.question.section]) result[a.question.section] = [];
    result[a.question.section]!.push(a);
  }
  return result;
};

interface FeedbackReportProps {
  currentUserType: UserType;
  programName: string | undefined;
  feedback: FeedbackDashboardCardProps;
}

const StyledDiv = styled.div`
  .content {
    padding: ${SPACERS.medium};
  }
`;

export interface FeedbackReportHandler {
  reloadReportAnswers: () => unknown;
}

const FeedbackReport = forwardRef<FeedbackReportHandler, FeedbackReportProps>(
  ({ feedback, programName, currentUserType }, forwardRef) => {
    const client = useClient();
    const [requestState, makeRequest] = useGraphQLRequest<
      AnswersBySectionIdResponse
    >();

    const reloadReportAnswers = useCallback(() => {
      client && makeRequest(getAnswersBySectionId(client, feedback.id));
    }, [client, feedback.id]);

    useEffect(() => {
      reloadReportAnswers();
    }, [client, feedback.id]);

    useImperativeHandle(forwardRef, () => ({
      reloadReportAnswers,
    }));

    const answers = useMemo(
      () => answersBySection(requestState.data?.allNpoReportAnswers || []),
      [requestState],
    );

    if (requestState.type === 'REQUEST_START') {
      return (
        <StyledDiv>
          <TrueImpactLoading className="content" />
        </StyledDiv>
      );
    }

    return (
      <StyledDiv>
        <FeedbackImpactReceipt
          isThirdParty={false}
          programName={feedback.title || feedback.portfolioName}
          reportId={feedback.reportId}
          answers={answers}
          feedback={feedback}
        />
      </StyledDiv>
    );
  },
);

export default FeedbackReport;
